/**
 * The Xonera mark as a loading indicator.
 *
 * A DERIVED MOTIF, NOT THE LOGO. `XoneraMark` is brand chrome and its docstring
 * fixes its colours deliberately; this deliberately breaks that rule, and the
 * separation is why it lives in its own file rather than behind an `animated`
 * prop. Two things differ:
 *
 *  - **No tile.** `FullScreenSpinner` is transparent so the page's GridBackground
 *    shows through. A `#000000` tile there punches a black hole in light mode and
 *    vanishes in dark (1.11:1 against `--ground`), which would leave the mark's
 *    `#D9D9D9` strokes sitting at ~1.4:1.
 *  - **`currentColor`.** With no field to sit on, the fixed grey has nothing to
 *    contrast against, so the caller supplies the colour — the same
 *    `text-brand-500 dark:text-primary` the `Loader2` it replaces already used.
 *
 * It also drops the mark's `scale(0.62)` inset, which existed only to sit the
 * artwork inside the tile. Ink then fills 86% of the box against `Loader2`'s
 * ~83%, so it is optically the same size as the icon it stands in for. The
 * stroke width is unchanged at 4.36 — the mark's transform scaled ink and stroke
 * together, so the weight-to-ink ratio is the same either way.
 *
 * THE MOTION. Each of the four ticks is a vertex with a long arm and a short
 * arm. The loop swaps those lengths and swaps them back. Be clear about what
 * that reads as: it is a two-beat pulse, NOT a rotation. At the half-cycle the
 * mark is its own mirror image, and a mirror is not a rotation, so no `rotate()`
 * composes with this into a seamless spin. If a spin is ever wanted, it has to
 * be added as a second, independent animation on the group.
 *
 * HOW LENGTH IS ANIMATED. Each polyline is split into two paths that both start
 * at the shared vertex and are both drawn at that tick's LONG length; the
 * visible length is then clipped with `stroke-dasharray` against
 * `pathLength="100"`. Drawing every arm long is what makes the union bbox
 * (x 4.43..59.57, y 4.18..59.82) IDENTICAL to the static mark's — the silhouette
 * never grows, so the motion cannot clip or shift layout.
 *
 * CONSTRAINT: each vertex now carries two overlapping round caps where the mark
 * had one round linejoin. Identical while the stroke is opaque — the caps cover
 * exactly the region the join did — but apply `stroke-opacity`, a gradient or a
 * blend mode and four bright dots appear at the vertices.
 *
 * COST: eight animating `stroke-dasharray` values force path re-tessellation per
 * frame on some engines. Fine for one instance; do not put this in a repeated
 * list row. One per screen.
 */
interface XoneraSpinnerProps {
  /** Sizing and colour, e.g. `'h-10 w-10 text-brand-500 dark:text-primary'`. */
  className?: string;
  /** Accessible name. The `<svg>` stays hidden and the name comes only from here. */
  label?: string;
  /**
   * Pause and seek to a fraction of the loop, 0..1.
   *
   * A test seam, not a feature: an infinitely-animating story cannot produce a
   * stable Chromatic snapshot, and this is the only way to give the catalog a
   * frame that is both deterministic AND meaningful (see the `Cycle` story,
   * which lays the whole loop out side by side). Also the determinism hook for
   * any Playwright assertion.
   */
  freezeAt?: number;
}

/**
 * The eight arms, as a table rather than eight hand-written elements.
 *
 * `d` always starts at the tick's vertex and runs to that tick's LONG length —
 * four of these are the mark's own arms unchanged, four are its short arms
 * extended along the same bearing. The 180° symmetry survives exactly:
 * `3A = 64 - 1B` and `4A = 64 - 2B`, componentwise.
 *
 * `cls` holds the FULL class name. It must not be built by template — Tailwind's
 * JIT scans source text, so `animate-xonera-tick-${pair}` compiles to no utility
 * at all. Same trap `Foundation/Logo` documents for `h-${n}`.
 *
 * `phase` is a fraction of the loop. Arms of the same rotational pair are LOCKED
 * to the same phase: tick 3 IS tick 1 rotated 180°, so letting them drift apart
 * would break the mark's symmetry and read as broken rather than animated. The
 * two pairs are a quarter-cycle apart, which is what makes the change travel
 * around the ring instead of the whole mark flashing at once.
 */
const ARMS = [
  {
    id: '1A',
    d: 'M33.19 25.46L11.84 4.18',
    cls: 'animate-xonera-tick-a',
    phase: 0,
  },
  {
    id: '1B',
    d: 'M33.19 25.46L55.09 4.74',
    cls: 'animate-xonera-tick-a',
    phase: -0.5,
  },
  {
    id: '3A',
    d: 'M30.81 38.54L8.91 59.26',
    cls: 'animate-xonera-tick-a',
    phase: -0.5,
  },
  {
    id: '3B',
    d: 'M30.81 38.54L52.16 59.82',
    cls: 'animate-xonera-tick-a',
    phase: 0,
  },
  {
    id: '2A',
    d: 'M39.45 32.63L59.57 12.42',
    cls: 'animate-xonera-tick-b',
    phase: -0.25,
  },
  {
    id: '2B',
    d: 'M39.45 32.63L59.16 53.24',
    cls: 'animate-xonera-tick-b',
    phase: -0.75,
  },
  {
    id: '4A',
    d: 'M24.55 31.37L4.84 10.76',
    cls: 'animate-xonera-tick-b',
    phase: -0.75,
  },
  {
    id: '4B',
    d: 'M24.55 31.37L4.43 51.58',
    cls: 'animate-xonera-tick-b',
    phase: -0.25,
  },
] as const;

export function XoneraSpinner({
  className = '',
  label = 'Loading…',
  freezeAt,
}: XoneraSpinnerProps) {
  const frozen = freezeAt !== undefined;

  return (
    <span role='status' className='inline-flex'>
      <svg
        viewBox='0 0 64 64'
        className={className}
        aria-hidden
        // Inherited by every arm, so the reduced-motion rule in globals.css can
        // retime the whole spinner — animation AND phase offsets — by
        // redefining this one variable on the arms.
        style={{ '--xm-loop': '1400ms' } as React.CSSProperties}
      >
        <g
          fill='none'
          stroke='currentColor'
          strokeWidth={4.36}
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          {ARMS.map(arm => (
            <path
              key={arm.id}
              d={arm.d}
              pathLength={100}
              className={arm.cls}
              style={{
                // A negative delay renders the arm as if it had already been
                // running that long, so `phase - freezeAt` seeks every arm to
                // the same point in the shared cycle. Expressed as calc() over
                // the variable, never a literal ms value: literals would keep
                // their 1400ms-derived offsets when reduced motion retimes the
                // loop, and the ticks would fall out of phase.
                animationDelay: `calc(var(--xm-loop) * ${
                  frozen ? arm.phase - freezeAt : arm.phase
                })`,
                animationPlayState: frozen ? 'paused' : undefined,
              }}
            />
          ))}
        </g>
      </svg>
      <span className='sr-only'>{label}</span>
    </span>
  );
}
